<template>
    <div class="mobile-preview-wrapper">
        <div class="phone-frame">
            <!-- Phone Notch/Speaker (Visual only) -->
            <div class="phone-notch"></div>
            
            <div class="screen-content custom-scrollbar">
                <!-- Header Section (Skeleton) -->
                <div class="app-header">
                    <div class="header-top">
                        <div class="skeleton-text w-50 mb-2"></div>
                        <div class="skeleton-avatar"></div>
                    </div>
                    <div class="skeleton-title w-75"></div>
                </div>

                <!-- Balance Card (Skeleton) -->
                <div class="balance-card-container">
                    <div class="balance-card">
                        <div class="skeleton-text w-40 mb-3"></div>
                        <div class="d-flex justify-content-between mt-3">
                            <div class="text-center">
                                <div class="skeleton-icon mb-1"></div>
                                <div class="skeleton-text w-100"></div>
                            </div>
                            <div class="text-center">
                                <div class="skeleton-icon mb-1"></div>
                                <div class="skeleton-text w-100"></div>
                            </div>
                            <div class="text-center">
                                <div class="skeleton-icon mb-1"></div>
                                <div class="skeleton-text w-100"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Promotions Section (Real Content) -->
                <div class="promotions-section">
                    <div class="d-flex justify-content-between align-items-center mb-3 px-3">
                        <h3 class="section-title">{{ title }}</h3>
                        <span class="view-all">View all</span>
                    </div>

                    <div class="horizontal-scroll-container ps-3">
                        <template v-if="items.length > 0">
                            <div 
                                v-for="item in items" 
                                :key="item.id" 
                                class="preview-item-wrapper me-3"
                            >
                                <component 
                                    :is="getItemComponent(item)"
                                    v-bind="getItemProps(item)"
                                    :is-locked="false"
                                />
                            </div>
                        </template>
                        <div v-else class="empty-state me-3">
                            <div class="empty-placeholder">
                                <i class="fas fa-ticket-alt mb-2 opacity-50"></i>
                                <small>No active promotions</small>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Recent Transaction (Skeleton) -->
                <div class="mt-4 px-3">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                        <div class="skeleton-title w-50"></div>
                        <div class="skeleton-text w-20"></div>
                    </div>
                     <div class="transaction-card-skeleton mb-2"></div>
                     <div class="transaction-card-skeleton mb-2"></div>
                </div>
            </div>

            <!-- Bottom Nav (Visual) -->
            <div class="bottom-nav">
                <div class="nav-indicator"></div>
            </div>
        </div>
    </div>
</template>

<script>
import MemberBenefitContent from '@/components/member/MemberBenefitContent.vue'
import MemberVoucherContent from '@/components/member/MemberVoucherContent.vue'

export default {
    name: 'MobilePreview',
    components: {
        MemberBenefitContent,
        MemberVoucherContent
    },
    props: {
        title: {
            type: String,
            default: 'Limited offers'
        },
        items: {
            type: Array,
            default: () => []
        }
    },
    methods: {
        getItemComponent(item) {
            return item.item_type === 'voucher' 
                ? 'MemberVoucherContent' 
                : 'MemberBenefitContent'
        },
        getItemProps(item) {
            // Helper to find image
            const getImageUrl = (i) => {
                return i.item_image_url || i.image_url || i.avatar?.image_url || i.image || ''
            }
            
            // Map the generic item data to specific props required by components
            if (item.item_type === 'voucher') {
                return {
                    voucher: {
                        name: item.item_name || item.name || 'Unknown Voucher',
                        point: item.point || 0, // Fallback if point not available
                        stock: item.stock,
                        is_limited: item.is_limited,
                        merchant: {
                            name: item.merchant_name || 'Merchant',
                            logo: { image_url: item.merchant_logo || '' }
                        },
                        avatar: {
                            image_url: getImageUrl(item)
                        }
                    }
                }
            } else {
                return {
                    benefit: {
                        name: item.item_name || item.name || 'Unknown Benefit',
                        merchant: {
                            name: item.merchant_name || 'Merchant'
                        },
                        branch: {
                            name: ''
                        },
                        avatar: {
                            image_url: getImageUrl(item)
                        }
                    }
                }
            }
        }
    }
}
</script>

<style scoped>
.mobile-preview-wrapper {
    display: flex;
    justify-content: center;
    padding: 20px 0;
}

.phone-frame {
    width: 360px;
    height: 720px;
    background: #F8F9FA;
    border: 14px solid #1F2937;
    border-radius: 40px;
    position: relative;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transform: scale(0.8);
    transform-origin: top center;
}

.phone-notch {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 24px;
    background: #1F2937;
    border-bottom-left-radius: 12px;
    border-bottom-right-radius: 12px;
    z-index: 10;
}

.screen-content {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    background: #F3F4F6;
    padding-bottom: 20px;
}

/* Custom Scrollbar */
.custom-scrollbar::-webkit-scrollbar {
    width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
    background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #D1D5DB;
    border-radius: 2px;
}

/* App Header */
.app-header {
    background: var(--primary-color, #F93E3E); /* Modern Red/Pink */
    padding: 40px 20px 80px;
    border-bottom-left-radius: 24px;
    border-bottom-right-radius: 24px;
    color: white;
}

.header-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 15px;
}

/* Skeletons */
.skeleton-text {
    height: 12px;
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
}
.skeleton-title {
    height: 24px;
    background: rgba(255, 255, 255, 0.4);
    border-radius: 4px;
}
.skeleton-avatar {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.4);
    border-radius: 50%;
}
.skeleton-icon {
    width: 32px;
    height: 32px;
    background: #E5E7EB;
    border-radius: 50%;
    margin: 0 auto;
}

/* Balance Card */
.balance-card-container {
    padding: 0 20px;
    margin-top: -60px;
    margin-bottom: 24px;
}

.balance-card {
    background: white;
    border-radius: 16px;
    padding: 20px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.balance-card .skeleton-text {
    background: #E5E7EB;
}

/* Promotions Section */
.section-title {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 70%;
}

.view-all {
    font-size: 14px;
    color: #6B7280;
    white-space: nowrap;
    flex-shrink: 0;
}

.horizontal-scroll-container {
    display: flex;
    overflow-x: auto;
    padding-bottom: 10px;
    /* Hide scrollbar for this section */
    -ms-overflow-style: none;
    scrollbar-width: none;
}
.horizontal-scroll-container::-webkit-scrollbar {
    display: none;
}

.preview-item-wrapper {
    flex-shrink: 0;
    transform: scale(0.9); /* Scale down slightly to fit nicely */
    transform-origin: top left;
    width: 252px; /* 280 * 0.9 */
    height: 218px; /* 242 * 0.9 */
}

/* Empty State */
.empty-placeholder {
    width: 280px;
    height: 200px;
    background: #E5E7EB;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #9CA3AF;
}

/* Bottom Nav */
.bottom-nav {
    height: 24px;
    background: white;
    display: flex;
    justify-content: center;
    align-items: center;
    border-top: 1px solid #E5E7EB;
}

.nav-indicator {
    width: 120px;
    height: 4px;
    background: #111827;
    border-radius: 2px;
}

.transaction-card-skeleton {
    height: 60px;
    background: #E5E7EB; /* Gray for skeleton on white background */
    border-radius: 12px;
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

.mt-4 .skeleton-title, .mt-4 .skeleton-text {
    background: #9CA3AF; /* darker gray for text on light bg */
}

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: .5; }
}
</style>
